import { FC } from 'react';

import { useFormik } from 'formik';

import { Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { validationSchema } from '../form';

import { FileSelector } from 'v2/components/fileSystem/fileSelector/FileSelector';
import { RewardsFormControl } from 'v2/components/form/rewardsFormControl/RewardsFormControl';
import { Switch } from 'v2/components/switch/Switch';
import { ImagesBreakPoints } from 'v2/types/ImagesBreakPoints';
import { TournamentGroupForm } from 'v2/types/TournamentGroupModel';

interface LevelFormDialogProps {
  title: string;
  isOpen: boolean;
  onClose: VoidFunction;
  initialValues: TournamentGroupForm;
  isSubmitting: boolean;
  onSubmit: (values: TournamentGroupForm, resetForm: VoidFunction) => void;
}

export const TournamentGroupFormDialog: FC<LevelFormDialogProps> = ({
  title,
  isOpen,
  onClose,
  initialValues,
  isSubmitting,
  onSubmit,
}) => {
  const formik = useFormik<TournamentGroupForm>({
    initialValues,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, { resetForm }) => {
      onSubmit(values, resetForm);
    },
    enableReinitialize: true,
    validationSchema,
  });

  const handleImagesSelectChange = (field: keyof ImagesBreakPoints) => (v: unknown) => {
    formik.setFieldValue(`images.${field}`, v);
  };

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" scroll="paper" fullWidth>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <form id="tournamentGroupForm" className="form" onSubmit={formik.handleSubmit}>
          <TextField
            name="name"
            value={formik.values.name}
            error={!!formik.errors.name}
            helperText={formik.errors.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Name"
            type="text"
            required
          />

          <TextField
            name="rewardValue"
            value={formik.values.rewardValue}
            error={!!formik.errors.rewardValue}
            helperText={formik.errors.rewardValue}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Reward Value"
            type="text"
            required
          />

          <div className="form-group">
            <Typography variant="subtitle1">Images</Typography>
          </div>

          <div className="form-group">
            <FileSelector
              value={formik.values.images.image390Id}
              error={formik.errors.images?.image390Id}
              label="390px"
              onSelect={handleImagesSelectChange('image390Id')}
              required
            />

            <FileSelector
              value={formik.values.images.image768Id ?? ''}
              error={formik.errors.images?.image768Id}
              label="768px"
              onSelect={handleImagesSelectChange('image768Id')}
            />

            <FileSelector
              value={formik.values.images.image1024Id}
              error={formik.errors.images?.image1024Id}
              label="1024px"
              onSelect={handleImagesSelectChange('image1024Id')}
              required
            />

            <FileSelector
              value={formik.values.images.image1300Id ?? ''}
              error={formik.errors.images?.image1300Id}
              label="1300px"
              onSelect={handleImagesSelectChange('image1300Id')}
            />
          </div>

          <Switch
            name="autoHalfPrizesEnabled"
            checked={formik.values.autoHalfPrizesEnabled}
            onChange={formik.handleChange}
            label="Auto 50% partner prizes"
          />

          <RewardsFormControl formik={formik} withCount />
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="error" variant="outlined">
          Cancel
        </Button>

        <Button form="tournamentGroupForm" type="submit" variant="contained" loading={isSubmitting}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
